import {
  type GridColDef,
  type GridColumnHeaderParams,
  type GridRenderCellParams,
  GRID_CHECKBOX_SELECTION_COL_DEF,
} from "@mui/x-data-grid-pro";
import { type GridApiPro } from "@mui/x-data-grid-pro/models/gridApiPro";
import Tooltip from "@mui/material/Tooltip";
import Button from "@mui/material/Button";
import Box from "@mui/material/Box";
import LockIcon from "@mui/icons-material/Lock";
import CheckIcon from "@mui/icons-material/Check";
import type {
  ICommercialFeedDataGridRow,
  CommercialReceptionBookViewPlan,
} from "../types";
import {
  GridCellCheckboxRenderer,
  GridHeaderCheckbox,
} from "@mui/x-data-grid-pro";
import { Link } from "react-router-dom";

// 商業登記フィード用のカラム定義
export const CommercialFeedDataGridColumns = {
  GridCheckBoxSelectionColDef: {
    ...GRID_CHECKBOX_SELECTION_COL_DEF,
    type: "string",
    width: 60,
    renderHeader: (params: GridColumnHeaderParams) => {
      return (
        <Tooltip title="チェックを入れると下表の行をすべて選択します">
          <GridHeaderCheckbox field={params.field} colDef={params.colDef} />
        </Tooltip>
      );
    },
    renderCell: (
      params: GridRenderCellParams<unknown, ICommercialFeedDataGridRow>
    ) => {
      const isSelectable = (params.api as GridApiPro).isRowSelectable(
        params.id
      );
      if (isSelectable) {
        return <GridCellCheckboxRenderer {...params} />;
      }
      return (
        <Tooltip
          title="会社法人等番号を正しく取得できないため選択できません"
          placement="right"
        >
          <span>
            <GridCellCheckboxRenderer {...params} />
          </span>
        </Tooltip>
      );
    },
  },
  Id: {
    field: "id",
    headerName: "ID",
    width: 80,
  },
  RequesterCommercialRegistrationNumber: {
    field: "requesterCommercialRegistrationNumber",
    headerName: "会社法人等番号",
    width: 160,
  },
  RequesterCorporateNumber: {
    field: "requesterCorporateNumber",
    headerName: "法人番号",
    width: 160,
  },
  RequesterCorporateName: {
    field: "requesterCorporateName",
    headerName: "法人名",
    width: 250,
  },
  IsCorporate: {
    field: "isCorporate",
    headerName: "商業/法人",
    width: 100,
  },
  CommercialRequesterTypeName: {
    field: "commercialRequesterTypeName",
    headerName: "種別",
    width: 100,
  },
  LegalAffairsBureauRequestDate: {
    field: "legalAffairsBureauRequestDate",
    headerName: "受付年月日",
    width: 120,
  },
  LegalAffairsBureauReceptionNumber: {
    field: "legalAffairsBureauReceptionNumber",
    headerName: "受付番号",
    width: 100,
  },
  ReceptionReasons: {
    field: "receptionReasons",
    headerName: "登記の種類",
    width: 180,
  },
  Remarks: {
    field: "remarks",
    headerName: "備考",
    width: 120,
  },
  PrefecturesName: {
    field: "prefecturesName",
    headerName: "都道府県",
    width: 100,
  },
  LegalAffairsBureauName: {
    field: "legalAffairsBureauName",
    headerName: "法務局",
    width: 120,
  },
} as const;

/**
 * 「不動産登記簿」検索列を生成する。
 * `isMaskGuardEnabled` が true の場合、未購入行（`!row.isPurchased`）では
 * 検索ボタンを無効化する（従量課金プラン向けのマスクガード）。
 * flat_monthly プランでは false を渡し、ガードなしで常に検索可能にする。
 */
const makeStoredPicturesSearchColumn = (
  isMaskGuardEnabled: boolean
): GridColDef => ({
  field: "storedPicturesSearch",
  headerName: "不動産登記簿",
  width: 100,
  sortable: false,
  filterable: false,
  disableExport: true,
  renderCell: (
    params: GridRenderCellParams<unknown, ICommercialFeedDataGridRow>
  ) => {
    const { row } = params;
    const hasName = !!row.requesterCorporateName;
    const disabledByMask = isMaskGuardEnabled && !row.isPurchased;
    const searchParams = new URLSearchParams();
    if (hasName) {
      searchParams.set("ownerName", row.requesterCorporateName);
    }
    const queryString = searchParams.toString();
    const to = queryString
      ? `/storedpictures?${queryString}`
      : "/storedpictures";

    return hasName && !disabledByMask ? (
      <Button
        component={Link}
        to={to}
        target="_blank"
        rel="noopener noreferrer"
        variant="outlined"
        size="small"
        sx={{ margin: "auto" }}
      >
        検索
      </Button>
    ) : (
      <Button variant="outlined" size="small" disabled sx={{ margin: "auto" }}>
        検索
      </Button>
    );
  },
});

/**
 * 「商業登記簿」取得列を生成する。
 * `isMaskGuardEnabled` が true の場合、未購入行（`!row.isPurchased`）では
 * 検索ボタンを無効化する（従量課金プラン向けのマスクガード）。
 * flat_monthly プランでは false を渡し、ガードなしで常に検索可能にする。
 */
const makeAcquireCommercialBookColumn = (
  isMaskGuardEnabled: boolean
): GridColDef => ({
  field: "acquireCommercialBook",
  headerName: "商業登記簿",
  width: 100,
  sortable: false,
  filterable: false,
  disableExport: true,
  renderCell: (
    params: GridRenderCellParams<unknown, ICommercialFeedDataGridRow>
  ) => {
    const { row } = params;

    const buttonProps = {
      variant: "outlined",
      size: "small",
      sx: { margin: "auto" },
    } as const;

    const disabledByMask = isMaskGuardEnabled && !row.isPurchased;
    if (!row.requesterCommercialRegistrationNumber || disabledByMask) {
      return (
        <Button {...buttonProps} disabled>
          検索
        </Button>
      );
    }

    const searchParams = new URLSearchParams();
    if (row.requesterCorporateName) {
      searchParams.set("companyName", row.requesterCorporateName);
    }
    searchParams.set(
      "corporateNumber",
      row.requesterCommercialRegistrationNumber
    );
    if (row.prefecturesId) {
      searchParams.set("prefecturesId", String(row.prefecturesId));
    }
    const to = `/commercialbook?${searchParams.toString()}`;

    return (
      <Button
        {...buttonProps}
        component={Link}
        to={to}
        target="_blank"
        rel="noopener noreferrer"
      >
        検索
      </Button>
    );
  },
});

export const makeFlatMonthlyColumns = (): GridColDef[] => [
  CommercialFeedDataGridColumns.GridCheckBoxSelectionColDef,
  CommercialFeedDataGridColumns.RequesterCommercialRegistrationNumber,
  CommercialFeedDataGridColumns.RequesterCorporateNumber,
  CommercialFeedDataGridColumns.RequesterCorporateName,
  CommercialFeedDataGridColumns.IsCorporate,
  CommercialFeedDataGridColumns.CommercialRequesterTypeName,
  CommercialFeedDataGridColumns.LegalAffairsBureauRequestDate,
  CommercialFeedDataGridColumns.LegalAffairsBureauReceptionNumber,
  CommercialFeedDataGridColumns.ReceptionReasons,
  CommercialFeedDataGridColumns.Remarks,
  CommercialFeedDataGridColumns.PrefecturesName,
  CommercialFeedDataGridColumns.LegalAffairsBureauName,
  makeAcquireCommercialBookColumn(false),
  makeStoredPicturesSearchColumn(false),
];

const makePayPerViewRequesterCommercialRegistrationNumberColumn =
  (): GridColDef => ({
    field: "requesterCommercialRegistrationNumber",
    headerName: "会社法人等番号",
    width: 160,
    renderCell: (
      params: GridRenderCellParams<unknown, ICommercialFeedDataGridRow>
    ) => {
      const { row } = params;
      const masked = !row.isPurchased;
      return (
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
          {masked ? (
            <LockIcon color="action" fontSize="small" />
          ) : (
            <CheckIcon color="action" fontSize="small" />
          )}
          <span>{row.requesterCommercialRegistrationNumber}</span>
        </Box>
      );
    },
  });

// 法人番号もマスク対象のため、会社法人等番号列と同じ見せ方に揃える。
// 値だけでは「マスクされている」のか「データが無い」のか区別が付かないため、
// 未購入なら鍵、購入済みならチェックを出す。マスク値（● 8個）はAPIが返した値をそのまま表示する。
const makePayPerViewRequesterCorporateNumberColumn = (): GridColDef => ({
  field: "requesterCorporateNumber",
  headerName: "法人番号",
  width: 160,
  renderCell: (
    params: GridRenderCellParams<unknown, ICommercialFeedDataGridRow>
  ) => {
    const { row } = params;
    const masked = !row.isPurchased;
    return (
      <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
        {masked ? (
          <LockIcon color="action" fontSize="small" />
        ) : (
          <CheckIcon color="action" fontSize="small" />
        )}
        <span>{row.requesterCorporateNumber}</span>
      </Box>
    );
  },
});

const makePayPerViewRequesterCorporateNameColumn = (
  onUnlockSingle: (id: number) => void
): GridColDef => ({
  field: "requesterCorporateName",
  headerName: "法人名",
  width: 300,
  renderCell: (
    params: GridRenderCellParams<unknown, ICommercialFeedDataGridRow>
  ) => {
    const { row } = params;
    // 法人番号は会社法人等番号から導出するため、現状は法人番号だけが埋まる行は生まれない。
    // それでも条件に含めるのは、導出関係が変わったときに「マスクされているのに
    // 解除導線が出ない」行を作らないため。バックエンドの is_mask_target と条件を揃えている。
    const hasRequesterInfo =
      row.requesterCorporateName !== "" ||
      row.requesterCommercialRegistrationNumber !== "" ||
      row.requesterCorporateNumber !== "";
    const showUnlockButton = !row.isPurchased && hasRequesterInfo;
    const showCheckmark = row.isPurchased;
    return (
      <Box
        sx={{ display: "flex", alignItems: "center", gap: 1, width: "100%" }}
      >
        {showCheckmark && <CheckIcon color="action" fontSize="small" />}
        <span>{row.requesterCorporateName}</span>
        {showUnlockButton && (
          <Button
            size="small"
            variant="outlined"
            startIcon={<LockIcon fontSize="inherit" />}
            onClick={(e) => {
              e.stopPropagation();
              onUnlockSingle(row.id);
            }}
            sx={{ flexShrink: 0, whiteSpace: "nowrap" }}
          >
            閲覧
          </Button>
        )}
      </Box>
    );
  },
});

export const makePayPerViewColumns = (
  onUnlockSingle: (id: number) => void
): GridColDef[] => [
  CommercialFeedDataGridColumns.GridCheckBoxSelectionColDef,
  makePayPerViewRequesterCommercialRegistrationNumberColumn(),
  makePayPerViewRequesterCorporateNumberColumn(),
  makePayPerViewRequesterCorporateNameColumn(onUnlockSingle),
  CommercialFeedDataGridColumns.IsCorporate,
  CommercialFeedDataGridColumns.CommercialRequesterTypeName,
  CommercialFeedDataGridColumns.LegalAffairsBureauRequestDate,
  CommercialFeedDataGridColumns.LegalAffairsBureauReceptionNumber,
  CommercialFeedDataGridColumns.ReceptionReasons,
  CommercialFeedDataGridColumns.Remarks,
  CommercialFeedDataGridColumns.PrefecturesName,
  CommercialFeedDataGridColumns.LegalAffairsBureauName,
  makeAcquireCommercialBookColumn(true),
  makeStoredPicturesSearchColumn(true),
];

// plan / row.isPurchased はどちらも表示制御（どのカラム構成・マスクUIを出すか）にのみ使う値であり、
// マスクの真の判定はサーバー側で行われる契約になっている。
// フィードAPIはリクエストユーザーの実際の契約プラン・購入履歴をDBで判定し、
// 未購入行の会社法人等番号・法人番号・法人名を ● にマスク済みの状態で返すため、
// クライアント側で plan（userData.commercialReceptionBookViewPlan）を改竄しても実データは取得できない。
export const makeCommercialRegistrationReceptionBookDataGridColumns = (
  plan: CommercialReceptionBookViewPlan = "pay_per_view",
  onUnlockSingle: (id: number) => void = () => undefined
): GridColDef[] => {
  if (plan === "flat_monthly") {
    return makeFlatMonthlyColumns();
  }
  return makePayPerViewColumns(onUnlockSingle);
};
